if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.fetchAvailability();
      }

      getVariantIds() {
        const variantIds = this.dataset.variantIds || this.dataset.variantId || '';
        return [...new Set(variantIds.split(',').map((id) => id.trim()).filter(Boolean))];
      }

      getVariantSectionUrl(variantId) {
        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        return `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;
      }

      get locationVariantPickerEnabled() {
        return this.dataset.enableLocationVariantPicker !== 'false';
      }

      get hasOnlyDefaultVariant() {
        return this.dataset.hasOnlyDefaultVariant === 'true' || this.getVariantIds().length === 1;
      }

      isHiddenPickupLocation(item) {
        return item.location_name?.trim().toLowerCase() === 'head office';
      }

      hasVisibleAvailablePickupLocation(items) {
        return items.some((item) => item.location_id && !this.isHiddenPickupLocation(item) && item.available);
      }

      fetchAvailability() {
        const variantIds = this.getVariantIds();
        if (!variantIds.length) return;

        Promise.allSettled(variantIds.map((variantId) => this.fetchVariantAvailability(variantId)))
          .then((results) => {
            const fulfilledResults = results.filter((result) => result.status === 'fulfilled');
            const items = fulfilledResults
              .flatMap((result) => result.value.items);
            const locationOrder = fulfilledResults
              .flatMap((result) => result.value.locationOrder)
              .filter((locationId, index, locationIds) => locationIds.indexOf(locationId) === index);

            if (!items.length || !this.hasVisibleAvailablePickupLocation(items)) {
              this.clearAvailability();
              return;
            }

            this.renderPreview(this.aggregatePickupAvailability(items, locationOrder));
          })
          .catch(() => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      fetchVariantAvailability(variantId) {
        return fetch(this.getVariantSectionUrl(variantId))
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            const availabilityData = sectionInnerHTML?.querySelector('[data-pickup-availability-items]');
            const items = availabilityData ? JSON.parse(availabilityData.textContent) : [];
            const locationOrder = items
              .filter((item) => item.location_id && !this.isHiddenPickupLocation(item))
              .map((item) => item.location_id);

            return { items, locationOrder };
          });
      }

      compareStockLocationOrder(locationA, locationB) {
        const sortIndexA = Number.isInteger(locationA.sort_index) ? locationA.sort_index : Number.MAX_SAFE_INTEGER;
        const sortIndexB = Number.isInteger(locationB.sort_index) ? locationB.sort_index : Number.MAX_SAFE_INTEGER;
        const sortIndexDiff = sortIndexA - sortIndexB;
        const nameDiff = (locationA.location_name || '').localeCompare(locationB.location_name || '', undefined, {
          sensitivity: 'base',
        });

        if (this.hasOnlyDefaultVariant) {
          return nameDiff || sortIndexDiff;
        }

        return sortIndexDiff || nameDiff;
      }

      selectPreferredLocation(locations) {
        const selectedLocationId = String(this.dataset.selectedLocationId || '');

        let selectedLocation = locations.find((location) => {
          return location.available && selectedLocationId && String(location.location_id) === selectedLocationId;
        });

        locations.forEach((location) => {
          location.selected = location === selectedLocation;
        });

        return selectedLocation;
      }

      aggregatePickupAvailability(items, locationOrder = []) {
        const locations = [];
        const locationsById = new Map();
        const locationOrderById = new Map(locationOrder.map((locationId, index) => [String(locationId), index]));
        let locationIndex = locationOrderById.size;
        let availabilityIndex = 0;
        const selectedVariantId = String(this.dataset.variantId || '');
        const useVariantAvailabilityOrder = !this.hasOnlyDefaultVariant && this.getVariantIds().length > 1;

        items.forEach((item) => {
          if (!item.location_id) return;
          if (this.isHiddenPickupLocation(item)) return;

          const locationOrderIndex = Number.isInteger(item.location_sort_index)
            ? item.location_sort_index
            : locationOrderById.has(String(item.location_id))
              ? locationOrderById.get(String(item.location_id))
              : locationIndex++;

          if (!locationsById.has(item.location_id)) {
            const location = {
              location_id: item.location_id,
              location_name: item.location_name,
              available: Boolean(item.available),
              variant_id: item.variant_id,
              serial: item.serial,
              postcode: item.postcode || '',
              options: item.options || [],
              selected: false,
              pick_up_time: item.available ? item.pick_up_time : '',
              address_html: item.address_html || '',
              email: item.email || '',
              phone: item.phone || '',
              sort_index: locationOrderIndex,
              available_sort_index: item.available ? availabilityIndex++ : null,
            };

            locationsById.set(item.location_id, location);
            locations.push(location);
            return;
          }

          const location = locationsById.get(item.location_id);

          if (!location.address_html && item.address_html) {
            location.address_html = item.address_html;
          }

          if (!location.email && item.email) {
            location.email = item.email;
          }

          if (!location.phone && item.phone) {
            location.phone = item.phone;
          }

          if (item.available && (!location.available || String(item.variant_id || '') === selectedVariantId)) {
            if (!location.available) {
              location.available_sort_index = availabilityIndex++;
            }

            location.available = true;
            location.variant_id = item.variant_id;
            location.serial = item.serial;
            location.options = item.options || [];
            location.pick_up_time = item.pick_up_time || '';
          }
        });

        locations.sort((locationA, locationB) => {
          if (locationA.available !== locationB.available) {
            return locationA.available ? -1 : 1;
          }

          if (locationA.available && locationB.available && useVariantAvailabilityOrder) {
            return locationA.available_sort_index - locationB.available_sort_index;
          }

          return this.compareStockLocationOrder(locationA, locationB);
        });

        const selectedLocation = this.selectPreferredLocation(locations);
        const closestLocation = selectedLocation || locations.find((location) => location.available) || locations[0];

        return {
          locations,
          closestLocation,
          selectedLocation,
        };
      }

      clearAvailability() {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();

        this.innerHTML = '';
        this.removeAttribute('available');
      }

      onClickRefreshList() {
        this.fetchAvailability();
      }

      update(variant) {
        if (variant?.id) {
          this.dataset.variantId = variant.id;
          this.fetchAvailability();
        } else {
          this.clearAvailability();
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      getPreviewMessage(template, locationName) {
        return template.replace('__location__', this.escapeHtml(locationName));
      }

      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value || '';
        return div.innerHTML;
      }

      buildPreviewMarkup({ selectedLocation, locations }) {
        const selectedStore = selectedLocation?.available ? selectedLocation : null;
        const isAvailable = locations.some((location) => location.available);
        const locationName = selectedStore?.location_name || '';
        const locationCount = locations.length;
        const previewMessage = isAvailable
          ? this.getPreviewMessage(this.dataset.pickUpAvailableAtTemplate, locationName)
          : this.getPreviewMessage(this.dataset.pickUpUnavailableAtTemplate, locationName);
        const storeNameTemplate =
          this.dataset.pickupStoreName || '{{ location_name }}';

        const storeName = storeNameTemplate.replace(
          '{{ location_name }}',
          this.escapeHtml(selectedStore?.location_name || '')
        );
        const postcode = this.escapeHtml(selectedStore?.postcode || '');
        const storeDisplayName = selectedStore
          ? postcode
            ? `${storeName}, ${postcode}`
            : storeName
          : '';


        let buttonMarkup = '';
        if (isAvailable || locationCount > 1) {
          const buttonText = selectedStore
            ? this.dataset.changeStoreText || this.dataset.checkOtherStoresText
            : locationCount === 1
              ? this.dataset.viewStoreInfoText
              : this.dataset.checkOtherStoresText;
          buttonMarkup = `
            <button
              id="ShowPickupAvailabilityDrawer"
              class="pickup-availability-button link link--text underlined-link"
              aria-haspopup="dialog"
            >
              ${this.escapeHtml(buttonText)}
            </button>
          `;
        }

        const shipInsteadMarkup = selectedStore
          ? `
            <button
              type="button"
              class="pickup-availability-clear-button link link--text underlined-link"
              data-clear-pickup-location
            >
              ${this.escapeHtml(this.dataset.shipInsteadText || 'Ship instead')}
            </button>
          `
          : '';

        return `
          <pickup-availability-preview class="pickup-availability-preview">
            <div class="pickup-availability-info">
              <p class="caption-large">${previewMessage}</p>
              <span class="pickup-availability-info-rightside">
                <span class="location-available">${storeDisplayName ? `${storeDisplayName}: ` : ''}</span>
                ${buttonMarkup}
                ${shipInsteadMarkup}
              </span>
            </div>
          </pickup-availability-preview>
        `;
      }

      buildLocationItemMarkup(location) {
        // const availabilityMarkup = location.available
        //   ? `
        //     <p class="pickup-availability-preview caption-large">
        //       <span class="svg-wrapper">
        //         ${this.dataset.tickIcon}
        //       </span>
        //       ${this.escapeHtml(this.dataset.pickUpAvailableText)}${
        //         location.pick_up_time ? `, ${this.escapeHtml(location.pick_up_time.toLowerCase())}` : ''
        //       }
        //     </p>
        //   `
        //   : `
        //     <p class="pickup-availability-preview caption-large pickup-availability-preview--unavailable">
        //       ${this.getPreviewMessage(this.dataset.pickUpUnavailableAtTemplate, location.location_name)}
        //     </p>
        //   `;

        const availabilityMarkup = '';

        const emailMarkup = location.email ? `<p>${this.escapeHtml(location.email)}</p>` : '';
        const phoneMarkup = location.phone ? `<p>${this.escapeHtml(location.phone)}</p>` : '';
        const locationInputMarkup = this.buildLocationInputMarkup(location);
        const locationSelectorMarkup = locationInputMarkup
          ? `
            <span class="pickup-location-selector pickup-location-selector--side">
              <span class="visually-hidden">Select ${this.escapeHtml(location.location_name)}</span>
              ${locationInputMarkup}
            </span>
          `
          : '';
        const locationDetailsMarkup = `
          <div class="pickup-location-details">
            <h3 class="h4">${this.escapeHtml(location.location_name)}</h3>
            ${availabilityMarkup}
            <div class="pickup-address-box">
              <address class="pickup-availability-address">
                ${location.address_html || ''}
                ${emailMarkup}
                ${phoneMarkup}
              </address>
            </div>
          </div>
          ${locationSelectorMarkup}
        `;
        const locationRowMarkup = locationInputMarkup
          ? `
            <label class="pickup-location-row pickup-location-row--selectable">
              ${locationDetailsMarkup}
            </label>
          `
          : `
            <div class="pickup-location-row">
              ${locationDetailsMarkup}
            </div>
          `;

        return `
          <li class="pickup-availability-list__item${
            location.available ? '' : ' pickup-availability-list__item--unavailable'}" 
             data-location-id="${location.location_id}"
             data-variant-id="${location.variant_id || ''}"
             data-location-name="${this.escapeHtml(location.location_name)}"
             data-location-postcode="${this.escapeHtml(location.postcode || '')}">
            ${locationRowMarkup}
          </li>
        `;
      }

      buildLocationInputMarkup(location) {
        if (!this.locationVariantPickerEnabled || !location.available || !location.variant_id) return '';

        return `
          <input
            type="radio"
            name="pickup_location"
            value="${this.escapeHtml(location.variant_id || '')}"
            data-serial="${this.escapeHtml(location.serial || '')}"
            data-options="${this.escapeHtml(JSON.stringify(location.options || []))}"
            ${location.available ? '' : 'disabled'}
            ${location.selected ? 'checked' : ''}
          >
        `;
      }

      buildDrawerMarkup(locations) {
        const availableStores = locations
        .filter(location => location.available)
        .map(location => location.location_name);

        const availableLocations = locations.filter(location => location.available);
        const unavailableLocations = locations.filter(location => !location.available);
        const hasSelectedLocation = locations.some(location => location.selected);

      const storeMessage = availableStores.length
        ? 'Available at the Showrooms below, please allow 48 hours'
        : 'Unavailable at the following Showrooms';
      const unavailableStoresMessage = 'Unavailable at the following Showrooms';
        return `
          <pickup-availability-drawer
            class="gradient"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="PickupAvailabilityHeading"
          >
            <div class="pickup-availability-header">
              <h2 class="h3 pickup-availability-drawer-title" id="PickupAvailabilityHeading">
                Store availability
              </h2>
              <button class="pickup-availability-drawer-button" type="button" aria-label="Close">
                <i class="flaticon-close"></i>
              </button>
            </div>
            <div class="availability__variant">
               <h3 class="availability__variant_header">
               <span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-tick" viewBox="0 0 20 20"><path fill="#108043" stroke="#fff" d="m16.136 6.979.003-.004a1.499 1.499 0 0 0-2.275-1.954l-5.945 6.777-1.858-1.859A1.499 1.499 0 1 0 3.94 12.06l2.999 3c.289.29.675.44 1.1.439h.019a1.5 1.5 0 0 0 1.08-.522z"></path></svg>
              </span>
               ${this.escapeHtml(storeMessage)}</h3>
            </div>
            <ul class="pickup-availability-list list-unstyled" role="list" data-store-availability-drawer-content>
                ${
                    availableLocations.length
                      ? `
                          ${availableLocations.map((location) => this.buildLocationItemMarkup(location)).join('')}
                    `
                      : ''
                  }

                  ${
                    unavailableLocations.length
                      ? `
                        <h4 class="pickup-location-group-title">
                         ${this.escapeHtml(unavailableStoresMessage)}
                        </h4>

                          ${unavailableLocations.map((location) => this.buildLocationItemMarkup(location)).join('')}
                    `
                      : ''
                  }

            </ul>
            ${
              hasSelectedLocation
                ? `
                  <div class="pickup-availability-actions">
                    <button
                      type="button"
                      class="pickup-availability-clear-button link link--text underlined-link"
                      data-clear-pickup-location
                    >
                      ${this.escapeHtml(this.dataset.shipInsteadText || 'Ship instead')}
                    </button>
                  </div>
                `
                : ''
            }
            <div class="pickup-availability-contact">
              <p class="pickup-availability-help-text">Need help ?</p>
              <a href="/pages/contact" class="pickup-availability-contact-button">
                Contact us
              </a>
            </div>
          </pickup-availability-drawer>
        `;
      }

      renderPreview(aggregatedAvailability) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();

        if (!aggregatedAvailability.closestLocation) {
          this.clearAvailability();
          return;
        }

        this.innerHTML = this.buildPreviewMarkup(aggregatedAvailability);
        this.setAttribute('available', '');

        document.body.insertAdjacentHTML('beforeend', this.buildDrawerMarkup(aggregatedAvailability.locations));
        const renderedDrawer = document.querySelector('pickup-availability-drawer');
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          renderedDrawer.classList.add(colorClass);
        });

        const button = this.querySelector('#ShowPickupAvailabilityDrawer');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);

        const overlayed = document.querySelector('pickup-availability-overlay');
        if (overlayed) {
          overlayed.classList.remove('is-visible');
        }
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);

        const overlayed = document.querySelector('pickup-availability-overlay');
        if (overlayed) {
          overlayed.classList.add('is-visible');
        }
      }
    }
  );
}

document.addEventListener('change', function (e) {

  if (!e.target.matches('[name="pickup_location"]')) return;

  const variantId = e.target.value;
  const variantOptions = parsePickupVariantOptions(e.target.dataset.options);

  const locationItem = e.target.closest('.pickup-availability-list__item');
  const locationName = locationItem?.dataset.locationName || '';
  const locationPostcode = locationItem?.dataset.locationPostcode || '';
  const pickupAvailability = document.querySelector('pickup-availability');

  if (pickupAvailability && locationItem?.dataset.locationId) {
    pickupAvailability.dataset.selectedLocationId = locationItem.dataset.locationId;
  }

  const previewLocation = document.querySelector(
    'pickup-availability .location-available'
  );

  if (previewLocation) {
    previewLocation.textContent = locationPostcode
      ? `${locationName}, ${locationPostcode}:`
      : `${locationName}:`;
  }

  const variantSelects = document.querySelector('variant-selects');

  updatePickupVariantInputs(variantId);

  if (variantSelects && variantOptions.length) {
    syncPickupVariantOptions(variantSelects, variantOptions);
  }

  const drawer = e.target.closest('pickup-availability-drawer');
  if (drawer) drawer.hide();

  if (pickupAvailability) {
    pickupAvailability.fetchAvailability();
  }

});

document.addEventListener('click', function (e) {
  const clearButton = e.target.closest('[data-clear-pickup-location]');
  if (!clearButton) return;

  e.preventDefault();

  const pickupAvailability = document.querySelector('pickup-availability');
  if (pickupAvailability) {
    delete pickupAvailability.dataset.selectedLocationId;
    pickupAvailability.fetchAvailability();
  }

  const drawer = clearButton.closest('pickup-availability-drawer');
  if (drawer) drawer.hide();
});

function parsePickupVariantOptions(options) {
  if (!options) return [];

  try {
    const parsedOptions = JSON.parse(options);
    return Array.isArray(parsedOptions) ? parsedOptions : [];
  } catch (error) {
    return [];
  }
}

function syncPickupVariantOptions(variantSelects, variantOptions) {
  const controls = variantSelects.querySelectorAll('select, fieldset');
  let changedControl = null;

  controls.forEach((control, index) => {
    const optionValue = variantOptions[index];
    if (!optionValue) return;

    if (control.matches('select')) {
      const matchingOption = Array.from(control.options).find((option) => option.value === optionValue);
      if (!matchingOption || matchingOption.disabled) return;

      control.value = matchingOption.value;
      Array.from(control.options).forEach((option) => option.removeAttribute('selected'));
      matchingOption.setAttribute('selected', 'selected');
      changedControl = control;
      return;
    }

    const matchingInput = Array.from(control.querySelectorAll('input[type="radio"]')).find((input) => {
      return input.value === optionValue && !input.disabled && !input.classList.contains('disabled');
    });

    if (!matchingInput) return;

    matchingInput.checked = true;
    changedControl = matchingInput;
  });

  if (changedControl) {
    changedControl.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function updatePickupVariantInputs(variantId) {
  document
    .querySelectorAll('.product-form input[name="id"]')
    .forEach((input) => {
      input.value = variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
}
